/**
 * SED pointer compare-and-set — the pure half of the client top-up guard.
 *
 * A site doc names its current SED with four fields: `sedId`,
 * `sedStoragePath`, `sedVersion` and `sedContentHash`. The client top-up
 * (`topUpSiteSed`) loads the object those fields name, saves a bumped body
 * back over it and then advances the fields. Before this guard it took the
 * fields from the tab's in-memory store copy and wrote them back
 * unconditionally, so a tab whose copy was stale (Athena, 2026-10: six days)
 * overwrote a superseded object and then stamped that object's hash onto a
 * pointer that had already moved to a different SED.
 *
 * The guard reads the LIVE head from the server, refuses when the store copy
 * disagrees or a server build owns the site, and advances the pointer only
 * inside a Firestore transaction that finds the same four fields it started
 * from. This module holds the comparisons both sides share. Pure: no I/O and
 * no store import, so the store, the services and tests can all use it.
 */

/** The four site-doc fields that together name the current SED bytes. */
export interface SedPointer {
  sedId: string | null;
  sedStoragePath: string | null;
  sedVersion: number | null;
  sedContentHash: string | null;
}

/** Any object carrying the pointer fields, e.g. a SiteEntity or a raw doc. */
export interface SedPointerFields {
  sedId?: unknown;
  sedStoragePath?: unknown;
  sedVersion?: unknown;
  sedContentHash?: unknown;
}

/** The fields that say a server build currently owns the site's SED. */
export interface SedBuildFields {
  sedStatus?: unknown;
  sedBootstrapLockStatus?: unknown;
  sedBootstrapLockedAt?: unknown;
}

/** Field names read from a Firestore snapshot with `snapshot.get(field)`. */
export const SED_POINTER_FIELDS = ['sedId', 'sedStoragePath', 'sedVersion', 'sedContentHash'] as const;
export const SED_BUILD_FIELDS = ['sedStatus', 'sedBootstrapLockStatus', 'sedBootstrapLockedAt'] as const;

function text(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** Normalise a pointer: undefined and '' become null, as does a non-finite version. */
export function sedPointerOf(fields: SedPointerFields | null | undefined): SedPointer {
  const version = fields?.sedVersion;
  return {
    sedId: text(fields?.sedId),
    sedStoragePath: text(fields?.sedStoragePath),
    sedVersion: typeof version === 'number' && Number.isFinite(version) ? version : null,
    sedContentHash: text(fields?.sedContentHash),
  };
}

/** Build a pointer from `snapshot.get`, so no raw `.data()` cast is needed. */
export function sedPointerFromGetter(get: (field: string) => unknown): SedPointer {
  return sedPointerOf(Object.fromEntries(SED_POINTER_FIELDS.map(field => [field, get(field)])));
}

/** The build fields read through `snapshot.get`. */
export function sedBuildFieldsFromGetter(get: (field: string) => unknown): SedBuildFields {
  return Object.fromEntries(SED_BUILD_FIELDS.map(field => [field, get(field)]));
}

/** All four fields must be identical. */
export function sameSedPointer(a: SedPointer, b: SedPointer): boolean {
  return a.sedId === b.sedId
    && a.sedStoragePath === b.sedStoragePath
    && a.sedVersion === b.sedVersion
    && a.sedContentHash === b.sedContentHash;
}

/**
 * The server build's lease length. Pinned equal to `LOCK_TTL_MS` in
 * `server/sedBootstrap/executeSedBootstrapJob.ts` by a test; it is copied
 * rather than imported so client code never pulls the server job module.
 */
export const SED_BOOTSTRAP_LOCK_TTL_MS = 90_000;

/**
 * True while a server build owns the site's SED: the status says
 * 'bootstrapping', or the lock is held and its lease is fresh. A held lock
 * with a missing or unparseable timestamp counts as fresh, matching the
 * server's `isLockActive`.
 */
export function sedBuildInProgress(fields: SedBuildFields | null | undefined, nowMs: number): boolean {
  if (fields?.sedStatus === 'bootstrapping') return true;
  if (fields?.sedBootstrapLockStatus !== 'locked') return false;
  const lockedAt = typeof fields.sedBootstrapLockedAt === 'string' ? Date.parse(fields.sedBootstrapLockedAt) : Number.NaN;
  if (!Number.isFinite(lockedAt)) return true;
  return nowMs - lockedAt < SED_BOOTSTRAP_LOCK_TTL_MS;
}

/** Why a top-up stopped without changing the site's evidence. */
export type SedTopUpRefusal =
  /** The live head could not be read (offline, permissions, missing doc). */
  | 'pointer-unverified'
  /** The tab's copy of the site names different evidence than the live doc. */
  | 'stale-store-pointer'
  /** A server build owns the site's evidence. */
  | 'build-in-progress'
  /** The stored bytes are not the version the site record names. */
  | 'body-mismatch'
  /** The live pointer changed while the top-up ran. */
  | 'pointer-moved'
  /** The pointer names a layout an in-place top-up must not write. */
  | 'save-path-mismatch'
  /** The stored object changed since this top-up loaded it. */
  | 'bytes-changed';

export const SED_TOP_UP_REFUSAL_MESSAGE: Record<SedTopUpRefusal, string> = {
  'pointer-unverified':
    'The current site evidence could not be confirmed with the server, so nothing was saved. Check your connection and try again.',
  'stale-store-pointer':
    'This page was showing older site evidence than the saved record, so nothing was saved. Reload the page and try again.',
  'build-in-progress':
    'A site evidence rebuild is running and owns the evidence, so nothing was saved. Try again once the rebuild finishes.',
  'body-mismatch':
    'The saved evidence does not match the version the site record names, so nothing was saved. Rebuild the site evidence to repair it.',
  'pointer-moved':
    'Site evidence changed in another session while this refresh ran, so nothing was saved. Reload the page and try again.',
  'save-path-mismatch':
    'This site\'s evidence is stored in a layout a refresh cannot update in place, so nothing was saved. Rebuild the site evidence instead.',
  'bytes-changed':
    'The saved evidence changed since this refresh loaded it, so nothing was saved. Reload the page and try again.',
};

/**
 * Thrown inside a top-up when it must stop without changing the site's
 * evidence. `topUpSiteSed` turns it into an `outcome: 'superseded'` result.
 */
export class SedTopUpSuperseded extends Error {
  readonly reason: SedTopUpRefusal;
  readonly sedVersion: number;

  constructor(reason: SedTopUpRefusal, sedVersion: number) {
    super(SED_TOP_UP_REFUSAL_MESSAGE[reason]);
    this.name = 'SedTopUpSuperseded';
    this.reason = reason;
    this.sedVersion = sedVersion;
  }
}

/** The live head as `readLiveSedHead` returns it. */
export interface LiveSedHead {
  pointer: SedPointer;
  buildInProgress: boolean;
}

/** Options for `commitSedPointerIfUnchanged`. */
export interface SedPointerCommitOptions<Patch> {
  /** Written only when no build holds the site. */
  statusPatch?: Patch;
  /** Refuse the whole write while a build holds the site. */
  requireIdle?: boolean;
  /** Receipt operation ID returned by `/api/storage/save` when staging a candidate SED. */
  candidateOperationId?: string;
  /** Stable operation ID for the `/api/sites/:siteId/sed-pointer-cas` publication call. */
  operationId?: string;
}

export type SedPointerCommitResult =
  | {
      committed: true;
      statusWritten: boolean;
      sedId?: string | null;
      sedVersion?: number | null;
      sedStoragePath?: string | null;
      sedStorageGeneration?: string | null;
      sedContentHash?: string | null;
      sedContentHashVersion?: number | null;
      sedCollectedAt?: string | null;
      historyVersion?: number;
      updatedAt?: string;
    }
  | { committed: false; reason: 'pointer-moved' | 'build-in-progress' | 'site-missing' };
